import {
  PaginatedTicketAlerts,
  PaginatedTicketLogs,
  PaginatedTickets,
  PauseTicketInput,
  Ticket
} from 'types/TicketTypes';
import useHttp, { useHttpWithoutRedirect } from '../helpers/HttpMethods';

const http = useHttp();
const httpNoRedirect = useHttpWithoutRedirect();

class TicketsService {
  /**
   * Busca os tickets paginados.
   * @param {string} filter - Filtros em string.
   * @returns {Promise<PaginatedTickets | Response>} - Tickets em formato paginado.
   */
  static async list(filter: string): Promise<PaginatedTickets | Response> {
    return await http.get(`/tickets?${filter}`);
  }

  /**
   * Busca os tickets no formato kanban.
   * @param {string} filter - Filtros em string.
   * @returns {Promise<any | Response>} - Tickets em formato kanban.
   */
  static async listKanban(filter: string): Promise<any | Response> {
    return await http.get(`/tickets/list-kanban?${filter}`);
  }

  /**
   * Busca os tickets de um cliente paginados.
   * @param {number} customerId - ID do cliente.
   * @param {string} filter - Filtros em string.
   * @returns {Promise<PaginatedTickets | Response>} - Tickets do cliente em formato paginado.
   */
  static async listByCustomer(
    customerId: number,
    filter: string
  ): Promise<PaginatedTickets | Response> {
    return await http.get(`/tickets/by-customer/${customerId}?${filter}`);
  }

  /**
   * Busca dados de um pré-ticket para criar um ticket.
   * @param {number} preticketId - ID do pré-ticket.
   * @returns {Promise<any | Response>} - Dados do pré-ticket.
   */
  static async getDataFromPreticket(
    preticketId: number
  ): Promise<any | Response> {
    return await http.get(`/tickets/from-preticket/${preticketId}`);
  }

  /**
   * Busca um ticket por ID.
   * @param {number} id - ID do ticket.
   * @returns {Promise<Ticket | Response>} - Dados completos do ticket.
   */
  static async findById(id: number): Promise<Ticket | Response> {
    return await http.get(`/tickets/${id}`);
  }

  /**
   * Busca os alertas de um ticket paginados.
   * @param {number} id - ID do ticket.
   * @param {string} filters - Filtros em string.
   * @returns {Promise<PaginatedTicketAlerts | Response>} - Alertas do ticket em formato paginado.
   */
  static async getAlerts(
    id: number,
    filters: string
  ): Promise<PaginatedTicketAlerts | Response> {
    return await http.get(`/tickets/${id}/alerts?${filters}`);
  }

  /**
   * Cria um novo ticket.
   * @param {FormData} body - Dados do ticket em FormData.
   * @returns {Promise<Ticket | Response>} - Ticket criado ou resposta de erro.
   */
  static async create(body: FormData): Promise<Ticket | Response> {
    return await http.postFormdata(body, '/tickets');
  }

  /**
   * Cria um ticket a partir de um pré-ticket.
   * @param {number} preticketid - ID do pré-ticket.
   * @param {FormData} body - Dados do ticket em FormData.
   * @returns {Promise<Ticket | Response>} - Ticket criado ou resposta de erro.
   */
  static async createFromPreticket(
    preticketid: number,
    body: FormData
  ): Promise<Ticket | Response> {
    return await http.postFormdata(
      body,
      `/tickets/from-preticket/${preticketid}`
    );
  }

  /**
   * Cria e completa um ticket a partir de um pré-ticket.
   * @param {number} preticketid - ID do pré-ticket.
   * @param {FormData} body - Dados do ticket em FormData.
   * @returns {Promise<Ticket | Response>} - Ticket criado e completado ou resposta de erro.
   */
  static async createFromPreticketAndComplete(
    preticketid: number,
    body: FormData
  ): Promise<Ticket | Response> {
    return await http.postFormdata(
      body,
      `/tickets/from-preticket-and-complete/${preticketid}`
    );
  }

  /**
   * Cria e completa um ticket.
   * @param {FormData} body - Dados do ticket em FormData.
   * @returns {Promise<Ticket | Response>} - Ticket criado e completado ou resposta de erro.
   */
  static async createAndComplete(body: FormData): Promise<Ticket | Response> {
    return await http.postFormdata(body, '/tickets/create-and-complete');
  }

  /**
   * Edita um ticket existente.
   * @param {number} id - ID do ticket a ser atualizado.
   * @param {any} body - Dados do ticket para atualização.
   * @returns {Promise<Ticket | Response>} - Ticket atualizado ou resposta de erro.
   */
  static async edit(id: number, body: any): Promise<Ticket | Response> {
    return await http.patch(body, `/tickets/${id}`);
  }

  /**
   * Remove um ticket.
   * @param {number} id - ID do ticket.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async delete(id: number): Promise<Response> {
    return await http.del(`/tickets/${id}`);
  }

  /**
   * Inicia um ticket.
   * @param {number} id - ID do ticket.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async start(id: number): Promise<Response> {
    return await http.putWithoutBody(`/tickets/${id}/start`);
  }

  /**
   * Pausa um ticket.
   * @param {number} id - ID do ticket.
   * @param {PauseTicketInput} body - Dados da pausa.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async pause(id: number, body: PauseTicketInput): Promise<Response> {
    return await httpNoRedirect.put(body, `/tickets/${id}/pause`);
  }

  /**
   * Completa um ticket.
   * @param {number} id - ID do ticket.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async complete(id: number): Promise<Response> {
    return await http.putWithoutBody(`/tickets/${id}/complete`);
  }

  /**
   * Reabre um ticket.
   * @param {number} id - ID do ticket.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async reopen(id: number): Promise<Response> {
    return await http.putWithoutBody(`/tickets/${id}/reopen`);
  }

  /**
   * Busca os logs de um ticket paginados.
   * @param {number} id - ID do ticket.
   * @param {string} filter - Filtros em string.
   * @returns {Promise<PaginatedTicketLogs | Response>} - Logs do ticket em formato paginado.
   */
  static async listLogs(
    id: number,
    filter: string
  ): Promise<PaginatedTicketLogs | Response> {
    return await http.get(`/tickets/${id}/logs?${filter}`);
  }

  /**
   * Busca o tempo gasto em um ticket.
   * @param {number} id - ID do ticket.
   * @returns {Promise<string | Response>} - Tempo gasto em formato texto.
   */
  static async getTimeSpent(id: number): Promise<string | Response> {
    return await http.getInFormatText(`/tickets/${id}/get-time-spent`);
  }

  /**
   * Gera PDF para impressão de um ticket.
   * @param {number} id - ID do ticket.
   * @returns {Promise<Blob | Response>} - PDF do ticket.
   */
  static async print(id: number): Promise<Blob | Response> {
    return await http.getPdf(`/tickets/${id}/print`);
  }
}

export default TicketsService;
